<template>
  <div class="w-full overflow-hidden">
    <section v-if="images.length > 0 || videos.length > 0" class="bg-white py-10 xl:py-12">
      <div class="container mx-auto v-page-section-container-padding">
        <AtomHeadingPageSection text-color="text-black" class="mb-5 sm:mb-6 xl:mb-8" :title="$t('event-detail-media-title')" />

        <!-- Scenario 1: When there is enough media to show a slider on all breakpoints -->
        <div v-if="images.length + videos.length > 2">
          <!--
            The slider is centred with a definite `md:w-10/12` width rather than a `md:col-span-10` grid
            item: with `slides-per-view="auto"` the slides size themselves off the slider's width, so it
            needs a resolved one. As a `1fr` grid item (with the column gap) that width collapses and every
            slide (and its media) renders at 0.
          -->
          <div class="relative lg:max-w-screen-lg lg:mx-auto xl:max-w-screen-xl xl:px-12">
            <Swiper
              :centered-slides="true"
              :speed="500"
              slides-per-view="auto"
              :loop="false"
              :pagination="{ el: '.slider-pagination', clickable: true, bulletElement: 'button' }"
              :a11y="a11y"
              :modules="[Pagination, A11y]"
              class="carousel-infinite relative mb-0 md:w-10/12 md:mx-auto"
              @swiper="onSwiperInit"
            >
              <SwiperSlide v-for="(video, i) in videos" :key="`vid${i}`" class="event-detail-videos !w-full">
                <MoleculeCardEventMedia :video="video.oEmbed.url" :caption="video.introduction" />
              </SwiperSlide>
              <SwiperSlide v-for="(image, i) in images" :key="`img${i}`" class="!w-full">
                <MoleculeCardEventMedia :image="image" :caption="image.copyright" />
              </SwiperSlide>
            </Swiper>
          </div>
          <!-- No template refs on the buttons: under <script setup> a `ref="next"` writes the element into
               the setup binding of the same name, silently replacing the next()/prev() handlers. They were
               leftovers from the Vue 2 version, where slick was driven through $refs. -->
          <div class="slider-navigation flex items-center justify-center mt-4 mb-4 relative z-10 lg:mb-8">
            <button class="order-1" :aria-label="t_('previous')" @click="prev()">
              <AtomIconArrowLeft class="w-4 h-4 text-app-primary-1-red--torch" />
            </button>
            <!-- Swiper's pagination replaces its target's innerHTML, so it needs its own element here
                 rather than the shared wrapper - pointing it at .slider-navigation wiped out the arrows. -->
            <div class="slider-pagination order-2 flex items-center mx-10"></div>
            <button class="order-3" :aria-label="t_('next')" @click="next()">
              <AtomIconArrowRight class="w-4 h-4 text-app-primary-1-red--torch" />
            </button>
          </div>
        </div>

        <!-- Scenario 2: When there are 2 items -->
        <div
          v-else-if="images.length + videos.length === 2"
          class="relative grid grid-cols-6 md:grid-cols-12 lg:max-w-screen-lg lg:mx-auto xl:max-w-screen-xl gap-y-4 sm:gap-5 lg:gap-8"
        >
          <MoleculeCardEventMedia
            v-for="(video, i) in videos"
            :key="`vid${i}`"
            :video="video.oEmbed.url"
            :caption="video.introduction"
            class="event-detail-videos relative mb-0 col-span-full lg:col-span-6"
          />
          <MoleculeCardEventMedia v-for="(image, i) in images" :key="`img${i}`" :image="image" :caption="image.copyright" class="relative mb-0 col-span-full lg:col-span-6" />
        </div>

        <!-- Scenario 3: When there is ony one video or 1 image -->
        <div v-else class="relative md:grid md:grid-cols-12 lg:max-w-screen-lg lg:mx-auto xl:max-w-screen-xl xl:px-12 lg:gap-x-16">
          <div class="relative mb-0 md:col-span-10 md:col-start-2">
            <MoleculeCardEventMedia v-if="videos.length === 1" :video="videos[0].oEmbed.url" :caption="videos[0].introduction" class="event-detail-videos w-full inline-block" />
            <MoleculeCardEventMedia v-if="images.length === 1" :image="images[0]" :caption="images[0].copyright" class="relative mb-0 col-span-full lg:col-span-6" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import type { Swiper as SwiperClass } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

defineProps({
  images: { type: Array as PropType<any[]>, required: false, default: () => [] },
  videos: { type: Array as PropType<any[]>, required: false, default: () => [] },
})

const { t_ } = setupTranslationPrefixer('EventDetailPageMedia')

// Swiper's A11y module labels and focuses the pagination buttons for us. `{{index}}` is Swiper's own
// placeholder, substituted per bullet, so it's concatenated here rather than kept in the translation -
// vue-i18n would otherwise try to parse the braces as an interpolation of its own.
const a11y = computed(() => ({
  paginationBulletMessage: `${t_('go-to-slide')} {{index}}`,
}))

const swiper = ref<SwiperClass | null>(null)

function onSwiperInit(swiperInstance: SwiperClass) {
  swiper.value = swiperInstance
}

function next() {
  swiper.value?.slideNext()
}

function prev() {
  swiper.value?.slidePrev()
}
</script>
